import { z } from 'zod';
import type { Modality } from '../contracts';
import { customStrategy, customStrategySchema, validateCustomDef, type CustomState, type CustomStrategyDef } from './custom';
import type { Strategy, StrategyContext } from './types';

/**
 * Two "Meus robôs" robots of the same market run as ONE automation: one session, one entry, gales,
 * meta and limit (the panel's). Each robot keeps its own reading of the market — loss virtual,
 * pauses after a cycle, operating window — and the first one whose trigger fires takes the round.
 * That robot owns the cycle until it ends: its gale side rule decides the gales, and only its own
 * pause / stop counters move with the result. The other one keeps reading meanwhile (its paper
 * entries still count) but never enters inside someone else's cycle.
 *
 * The robots' definitions are copied into the configuration when it is saved (from the user's
 * saved robots, by the server), so a running session never changes under the user's feet.
 */

export const COMBO_STRATEGY_KEY = 'custom_combo';
/** Robots one automation can combine. */
export const COMBO_SIZE = 2;

export const comboRobotSchema = z.object({ id: z.string().min(1).max(40), name: z.string().min(1).max(60), def: customStrategySchema });

export const comboSchema = z.object({
  v: z.literal(1).default(1),
  robots: z.array(comboRobotSchema).length(COMBO_SIZE),
  /** Rush: the exit picked on the panel, shared by both robots (the runner reads it here). */
  accu: customStrategySchema.shape.accu,
});
export type ComboParams = z.infer<typeof comboSchema>;

export interface ComboState {
  /** Each robot's own state, in the order of `robots`. */
  subs: CustomState[];
  /** Robot running the current cycle (null = none: the next trigger takes it). */
  owner: number | null;
  /** What the automation is doing now, for the user's screen. */
  note: string;
}

const robotDef = (p: ComboParams, i: number): CustomStrategyDef => {
  const def = p.robots[i]!.def;
  // Rush: both robots read their paper entries against the exit chosen on the panel.
  return p.accu ? { ...def, accu: p.accu } : def;
};

function stateOf(raw: unknown, p: ComboParams, modality: Modality): ComboState {
  const st = (raw ?? {}) as Partial<ComboState>;
  const subs = p.robots.map((r, i) => (st.subs?.[i] as CustomState | undefined) ?? customStrategy.initialState(r.def, modality));
  const owner = typeof st.owner === 'number' && st.owner >= 0 && st.owner < p.robots.length ? st.owner : null;
  return { subs, owner, note: st.note ?? 'Lendo o mercado' };
}

/** "Robô A" shortened for the status line. */
const short = (name: string) => (name.length > 22 ? `${name.slice(0, 21).trimEnd()}…` : name);

export const comboStrategy: Strategy<ComboParams, ComboState> = {
  key: COMBO_STRATEGY_KEY,
  name: 'Robôs combinados',
  description: 'Dois robôs de "Meus robôs" numa só automação: entra no primeiro gatilho que aparecer.',
  modalities: ['EVEN_ODD', 'RISE_FALL', 'ACCUMULATOR'],
  paramsSchema: comboSchema as unknown as z.ZodType<ComboParams>,
  // No defaults: the robots come from the user's saved ones (listed with no robots).
  defaultParams: () => ({ v: 1, robots: [] }) as unknown as ComboParams,
  initialState: (p, modality) => ({ subs: p.robots.map((r) => customStrategy.initialState(r.def, modality)), owner: null, note: 'Lendo o mercado' }),

  validate: (p, modality) => {
    if (new Set(p.robots.map((r) => r.id)).size !== p.robots.length) return 'Escolha robôs diferentes para combinar.';
    if (modality === 'ACCUMULATOR' && !p.accu) return 'Escolha o saque do Rush.';
    for (const [i, r] of p.robots.entries()) {
      const error = validateCustomDef(robotDef(p, i), modality);
      if (error) return `${r.name}: ${error}`;
    }
    return null;
  },

  onTick: (ctx, tick) => {
    const p = ctx.params;
    const st = stateOf(ctx.state, p, ctx.modality);
    const level = ctx.level ?? 0;
    const sub = (i: number, lvl: number): StrategyContext<CustomStrategyDef, CustomState> => ({ ...ctx, params: robotDef(p, i), state: st.subs[i]!, level: lvl });

    // Inside a cycle: its robot decides the gale; the others only keep reading (paper entries, pauses).
    if (level > 0) {
      const owner = st.owner ?? 0;
      const subs = st.subs.map((s, i) => {
        if (i === owner) return s;
        const ev = customStrategy.onTick(sub(i, 0), tick);
        // A trigger of this robot inside another's cycle is not an entry: keep its last real side.
        return ev.intent ? { ...ev.state, last: s.last, entry: s.entry ?? null } : ev.state;
      });
      const ev = customStrategy.onTick(sub(owner, level), tick);
      subs[owner] = ev.state;
      const name = p.robots[owner]!.name;
      return { intent: ev.intent, state: { subs, owner, note: `${short(name)} · ${ev.state.note}` }, reason: `${name} · ${ev.reason}` };
    }

    // Between cycles: every robot reads the round; the first with a trigger takes it.
    const evs = st.subs.map((_, i) => customStrategy.onTick(sub(i, 0), tick));
    const taker = evs.findIndex((ev) => ev.intent);
    const subs = evs.map((ev, i) => (ev.intent && i !== taker ? { ...ev.state, last: st.subs[i]!.last, entry: st.subs[i]!.entry ?? null } : ev.state));
    if (taker >= 0) {
      const name = p.robots[taker]!.name;
      const ev = evs[taker]!;
      return { intent: ev.intent, state: { subs, owner: taker, note: `${short(name)} · ${ev.state.note}` }, reason: `${name} · ${ev.reason}` };
    }
    // Nothing to enter: what each robot is waiting for.
    const notes = evs.map((ev, i) => `${short(p.robots[i]!.name)}: ${ev.state.note}`);
    return { intent: null, state: { subs, owner: null, note: notes.join(' · ') }, reason: notes.join(' · ') };
  },

  onSettlement: (ctx, settled) => {
    const p = ctx.params;
    const st = stateOf(ctx.state, p, ctx.modality);
    const owner = st.owner ?? 0;
    const subs = [...st.subs];
    subs[owner] = customStrategy.onSettlement({ ...ctx, params: robotDef(p, owner), state: st.subs[owner]! }, settled);
    const note = `${short(p.robots[owner]!.name)} · ${subs[owner]!.note}`;
    // The cycle is over: the next trigger, of either robot, starts the next one.
    return { subs, owner: settled.chainOver ? null : owner, note };
  },

  // A stop set on either robot ends the automation (stops protect the user: never ignored).
  shouldEnd: (state, p) => {
    const subs = (state as Partial<ComboState> | null)?.subs ?? [];
    for (const [i, r] of p.robots.entries()) {
      const s = subs[i];
      const why = s ? customStrategy.shouldEnd?.(s, robotDef(p, i)) : null;
      if (why) return `${r.name}: ${why}`;
    }
    return null;
  },

  statusNote: (state) => state?.note ?? null,
};
